import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Registers global keyboard shortcuts.
 *
 * Leader-key pattern:
 *   Press `G`, then within LEADER_TIMEOUT_MS press a target key to navigate.
 * Standalone:
 *   `?` (Shift+/) opens the shortcut help dialog.
 *
 * Bindings (all lowercase; matched case-insensitively):
 *   g d  →  /dashboard
 *   g k  →  /calculator   (K = "kalculator", avoids conflict with "c" copy)
 *   g l  →  /library
 *   g i  →  /inventory
 *   g y  →  /cycles       (Y = cycles' 2nd char)
 *   g w  →  /weight
 *   g h  →  /history
 *   g m  →  /community
 *   g p  →  /playbook
 *   g s  →  /admin/settings  (admin only)
 *   g u  →  /admin/users     (admin only)
 *   g r  →  /profile
 *
 * The listener is disabled while the user is typing in an input, textarea,
 * contentEditable region, or when a modifier (ctrl/meta/alt) is held.
 */
const LEADER_TIMEOUT_MS = 1500;

const BINDINGS_BASE = {
  d: "/dashboard",
  k: "/calculators",
  l: "/library",
  i: "/inventory",
  y: "/cycles",
  w: "/weight",
  h: "/history",
  m: "/community",
  p: "/playbook",
  r: "/profile",
};

const BINDINGS_ADMIN = {
  s: "/admin/settings",
  u: "/admin/users",
};

function isTypingContext(target) {
  if (!target) return false;
  const tag = (target.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  // Skip when inside a rich text or any element that opts out
  if (target.closest && target.closest("[data-shortcut-ignore]")) return true;
  return false;
}

export default function useKeyboardShortcuts({ isAdmin = false, onHelpOpen } = {}) {
  const navigate = useNavigate();
  const leaderActive = useRef(false);
  const leaderTimer = useRef(null);

  useEffect(() => {
    const clearLeader = () => {
      leaderActive.current = false;
      if (leaderTimer.current) {
        clearTimeout(leaderTimer.current);
        leaderTimer.current = null;
      }
    };

    const handler = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingContext(e.target)) return;

      const key = (e.key || "").toLowerCase();

      // `?` opens the help modal (shift+/ produces `?`)
      if (key === "?" && typeof onHelpOpen === "function") {
        e.preventDefault();
        onHelpOpen();
        clearLeader();
        return;
      }

      if (leaderActive.current) {
        const route = BINDINGS_BASE[key] || (isAdmin ? BINDINGS_ADMIN[key] : undefined);
        clearLeader();
        if (route) {
          e.preventDefault();
          navigate(route);
        }
        return;
      }

      if (key === "g") {
        e.preventDefault();
        leaderActive.current = true;
        leaderTimer.current = setTimeout(clearLeader, LEADER_TIMEOUT_MS);
      }
    };

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      clearLeader();
    };
  }, [navigate, isAdmin, onHelpOpen]);
}

export const SHORTCUT_LIST = [
  { keys: ["G", "D"], label: "Dashboard" },
  { keys: ["G", "K"], label: "Calculator" },
  { keys: ["G", "L"], label: "Library" },
  { keys: ["G", "I"], label: "Inventory" },
  { keys: ["G", "Y"], label: "Cycles" },
  { keys: ["G", "W"], label: "Weight" },
  { keys: ["G", "H"], label: "History" },
  { keys: ["G", "M"], label: "Community" },
  { keys: ["G", "P"], label: "Playbook" },
  { keys: ["G", "R"], label: "Profile" },
];

export const SHORTCUT_LIST_ADMIN = [
  { keys: ["G", "U"], label: "Admin · Users" },
  { keys: ["G", "S"], label: "Admin · Site Settings" },
];
